import { FC, MutableRefObject, PropsWithChildren, useCallback, useEffect, useRef } from 'react';
import isEqual from 'lodash/isEqual';
import { useHistory, useLocation } from 'react-router';
import { usePreviousProps } from '../../../hooks/usePreviousProps';
import { useProductFilters } from '../../filter/useProductFilters';

export const ScrollRestoration: FC<PropsWithChildren> = ({ children }) => {
    const { filters } = useProductFilters();

    const location = useLocation();
    const history = useHistory();

    const previousFilterChange = usePreviousProps(filters);
    const previousLocationKey = usePreviousProps(location.key);

    const filterWillChange = !isEqual(previousFilterChange, filters);

    const scrollY = useRef<number | string>(0);
    const timeStarted = useRef(Date.now()) as MutableRefObject<number>;

    const isPOP = history.action === 'POP';
    const isPush = history.action === 'PUSH';

    const restoredScrollPos = window.sessionStorage.getItem(location.key ?? '') || 0;
    const currentScrollPosition = window.scrollY || document.documentElement.scrollTop;

    const restoreScroll = useCallback(() => {
        window.scrollTo(0, parseInt(scrollY.current.toString()));
        if (!isEqual(String(window.scrollY), scrollY) && timeStarted.current + 2000 > Date.now()) {
            window.requestAnimationFrame(restoreScroll);
        }
    }, [scrollY, timeStarted]);

    useEffect(() => {
        if (filterWillChange) {
            scrollY.current = currentScrollPosition;
        } else if (previousLocationKey && isPush) {
            window.sessionStorage.setItem(previousLocationKey, currentScrollPosition.toString());
        } else if (isPOP) {
            scrollY.current = restoredScrollPos;
        }

        if (isPOP) {
            timeStarted.current = Date.now();
            window.requestAnimationFrame(restoreScroll);
        }
    }, [currentScrollPosition, filterWillChange, isPOP, isPush, previousLocationKey, restoreScroll, restoredScrollPos]);

    return <>{children}</>;
};
